import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, FileUp, FileText, LogOut, Menu, X, Trash2, History,
  Users, ChevronsUpDown, Bell, Settings, Eye, GitCompareArrows, BookOpen, BarChart3, ReceiptText, Lock, Sparkles, MessageSquare, ShieldCheck, UserCog, FileSignature,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/AuthContext";
import { useWorkspace } from "../lib/WorkspaceContext";
import { usePlan } from "../lib/usePlan";
import UpgradeModal from "./UpgradeModal";
import LanguageSwitcher from "../i18n/LanguageSwitcher";
import QenvaWordmark from "./brand/QenvaWordmark";
import HelpButton from "./HelpButton";
import HelpPanel from "./HelpPanel";
import MfaGraceBanner from "./MfaGraceBanner";
import EmailVerificationBanner from "./EmailVerificationBanner";

function WorkspaceSwitcher() {
  const { workspaces, current, currentId, switchTo } = useWorkspace();
  const [open, setOpen] = useState(false);
  if (!current) return null;
  return (
    <div className="relative mb-4">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="workspace-switcher-button"
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-qenva-navy-line bg-qenva-navy-soft hover:bg-[#13325A] transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0">
          <Users size={14} className="text-white/60" />
          <span className="text-sm font-medium text-white truncate" data-testid="current-workspace-name">
            {current.name}
          </span>
        </span>
        <ChevronsUpDown size={13} className="text-white/40 shrink-0" />
      </button>
      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-30 rounded-lg border border-zinc-200 bg-white shadow-lg overflow-hidden"
          data-testid="workspace-switcher-menu"
        >
          <ul className="max-h-64 overflow-auto">
            {workspaces.map((w) => (
              <li key={w.id}>
                <button
                  onClick={() => { switchTo(w.id); setOpen(false); }}
                  data-testid={`workspace-switcher-item-${w.id}`}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex items-center justify-between gap-2 ${
                    w.id === currentId ? "bg-zinc-50 font-medium" : ""
                  }`}
                >
                  <span className="truncate">{w.name}</span>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400">{w.role}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { canAccess } = usePlan();
  const { current: currentWorkspace, workspaces: allWorkspaces, switchTo } = useWorkspace();
  // Single modal — opened by clicking any locked nav item. `lockedItem` holds
  // the feature key + Icon so the modal can render the right copy & glyph.
  const [lockedItem, setLockedItem] = useState(null);
  // Help panel — deliberately separate from lockedItem/UpgradeModal state so
  // it stays fully independent of nav-item plan-gating logic.
  const [helpPanelOpen, setHelpPanelOpen] = useState(false);

  // Server-side plan gate (2026-08-23). api.js turns any 403
  // plan_upgrade_required response into this event; reuse the same
  // lockedItem/UpgradeModal plumbing as nav clicks. feature defaults to a
  // Sentinel-only key (not null) because minPlanFor(null) falls back to the
  // lowest plan and would advertise the wrong tier in the modal copy.
  useEffect(() => {
    const onUpgrade = () => setLockedItem({ feature: "crosscheck", Icon: Lock, testId: "api-gate" });
    window.addEventListener("qenva:plan-upgrade-required", onUpgrade);
    return () => window.removeEventListener("qenva:plan-upgrade-required", onUpgrade);
  }, []);

  const handleSignOut = async () => { await logout(); navigate("/login"); };

  // Full-screen lockout — mirrors the 423 the backend returns from every
  // route that resolves this workspace via get_workspace_context (see
  // workspaces._resolve_workspace). `current` comes from GET /workspaces,
  // which stays reachable even when suspended, so this renders BEFORE any
  // page tries to hit a blocked endpoint. Offers a way out (switch to
  // another workspace, sign out) rather than a true dead end.
  if (currentWorkspace && currentWorkspace.status === "suspended") {
    const otherWorkspaces = allWorkspaces.filter(
      (w) => w.id !== currentWorkspace.id && w.status !== "suspended",
    );
    return (
      <div
        className="min-h-screen bg-qenva-off text-qenva-navy grid place-items-center px-6"
        data-testid="workspace-suspended-screen"
      >
        <div className="max-w-md w-full text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-50 flex items-center justify-center mb-5">
            <Lock size={26} className="text-rose-500" strokeWidth={1.75} />
          </div>
          <h1 className="text-xl font-semibold text-zinc-900 mb-2" data-testid="workspace-suspended-title">
            {t("workspace_suspended.title")}
          </h1>
          <p className="text-sm text-zinc-600 mb-6">
            {t("workspace_suspended.body", { workspace: currentWorkspace.name })}
          </p>
          {otherWorkspaces.length > 0 && (
            <div
              className="rounded-xl border border-zinc-200 bg-white p-4 text-left mb-4"
              data-testid="workspace-suspended-switcher"
            >
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">
                {t("workspace_suspended.switch_label")}
              </p>
              <div className="space-y-1.5">
                {otherWorkspaces.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => switchTo(w.id)}
                    data-testid={`workspace-suspended-switch-${w.id}`}
                    className="w-full text-left text-sm px-3 py-2 rounded-lg border border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 transition-colors truncate"
                  >
                    {w.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={handleSignOut}
            data-testid="workspace-suspended-sign-out"
            className="text-sm text-zinc-500 hover:text-zinc-800 underline underline-offset-2"
          >
            {t("nav.sign_out")}
          </button>
        </div>
      </div>
    );
  }

  const navItems = [
    { to: "/dashboard",  label: t("nav.overview"),           Icon: LayoutDashboard,  testId: "nav-dashboard",          feature: "dashboard_basic" },
    { to: "/alerts/register", label: t("nav.under_watch"),   Icon: Eye,              testId: "nav-under-watch",        feature: "under_watch_full" },
    { to: "/documents",  label: t("nav.documents"),          Icon: FileText,         testId: "nav-documents",          feature: "documents" },
    { to: "/pre-signature", label: t("nav.pre_signature"),   Icon: FileSignature,    testId: "nav-pre-signature",      feature: "pre_signature" },
    { to: "/comparisons", label: t("nav.crosscheck"),         Icon: GitCompareArrows, testId: "nav-crosscheck",         feature: "crosscheck" },
    { to: "/reports/executive", label: t("nav.executive_report"), Icon: BookOpen,    testId: "nav-executive-report",   feature: "executive_report" },
    { to: "/reports/vigilance",  label: t("nav.reports"),            Icon: BarChart3,        testId: "nav-reports",           feature: "rapports_vigilance" },
    { to: "/invoices/summary",   label: t("nav.invoices_summary"),   Icon: ReceiptText,      testId: "nav-invoices-summary",  feature: "invoices_summary" },
    { to: "/alerts",     label: t("nav.alerts"),             Icon: Bell,             testId: "nav-alerts",             feature: "alerts" },
    { to: "/chat",       label: t("nav.chat"),               Icon: MessageSquare,    testId: "nav-chat",               feature: "documents" },
    { to: "/settings/notifications", label: t("nav.settings"), Icon: Settings,       testId: "nav-settings" },
    { to: "/settings/security", label: t("settings.security.nav_label"), Icon: ShieldCheck, testId: "nav-security" },
    { to: "/settings/profile", label: t("settings.profile.nav_label"), Icon: UserCog, testId: "nav-profile" },
  ];

  // Secondary nav (less prominent) — accessible but discreet
  const isSuperuser = user && user.role === "admin";
  const secondaryNavItems = [
    { to: "/upload",     label: t("nav.upload"),     Icon: FileUp,    testId: "nav-upload" },
    { to: "/trash",      label: t("nav.trash"),      Icon: Trash2,    testId: "nav-trash" },
    { to: "/activity",   label: t("nav.activity"),   Icon: History,   testId: "nav-activity" },
    { to: "/workspaces", label: t("nav.workspaces"), Icon: Users,     testId: "nav-workspaces" },
    { to: "/pricing",    label: t("nav.pricing"),    Icon: Sparkles,  testId: "nav-pricing" },
    ...(isSuperuser
      ? [{ to: "/admin", label: t("nav.admin"), Icon: ShieldCheck, testId: "nav-admin" }]
      : []),
  ];

  const NavInner = ({ variant = "desktop" }) => (
    <>
      {/* Iter87 — Brand zone constrained to ≤15% of sidebar visible height.
          Desktop: w-[100px] logo (h≈134 via natural 0.747 portrait ratio) +
          py-3 (24 px total) → zone height ≈158 px → 14.6% of 1080 px viewport.
          Mobile drawer: w-[72px] logo (h≈96) + py-3 → zone ≈120 px → 13.3% of
          900 px viewport. object-fit contain, width fixed + height auto,
          zero distortion. */}
      <div
        className="-mx-5 -mt-5 lg:-mt-6 mb-5 px-5 py-3 flex items-center justify-center border-b border-black/10"
        style={{ background: "#FFFFFF" }}
        data-testid="sidebar-brand-zone"
      >
        <Link to="/dashboard" className="block" data-testid="brand-link" aria-label="QENVA · The Silent Guardian">
          <img
            src="/brand/v2-stacked-logo.png"
            alt="QENVA · The Silent Guardian"
            draggable={false}
            className={`${variant === "mobile" ? "w-[72px]" : "w-[100px]"} h-auto block select-none`}
            style={{
              objectFit: "contain",
              border: "none",
              outline: "none",
              background: "transparent",
              boxShadow: "none",
            }}
            data-testid="sidebar-stacked-logo"
          />
        </Link>
      </div>

      {/* Primary nav */}
      <nav className="flex-1 space-y-0.5 min-w-0">
        <WorkspaceSwitcher />

        {navItems.map(({ to, label, Icon, testId, soon, feature }) => {
          if (soon) {
            return (
              <div
                key={to}
                data-testid={testId}
                title={t("nav.soon_tooltip")}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] text-white/35 cursor-not-allowed select-none min-w-0"
              >
                <Icon size={17} strokeWidth={1.75} className="shrink-0" />
                <span className="flex-1 truncate">{label}</span>
                <span className="text-[9px] uppercase tracking-[0.16em] font-bold px-1.5 py-0.5 rounded bg-white/5 text-white/40 shrink-0">
                  {t("nav.soon_badge")}
                </span>
              </div>
            );
          }
          // Plan-based gating: render a locked, click-to-upgrade row when the
          // current plan doesn't unlock this feature. Keep the layout exactly
          // identical to the unlocked row to avoid layout shifts when the
          // user upgrades.
          const locked = feature && !canAccess(feature);
          if (locked) {
            return (
              <button
                key={to}
                type="button"
                onClick={() => {
                  setLockedItem({ feature, Icon, testId });
                  setOpen(false);
                }}
                data-testid={`${testId}-locked`}
                title={t("nav.locked_tooltip")}
                className="w-full group flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] text-white/40 hover:text-white/60 hover:bg-white/5 transition-colors min-w-0 text-left"
              >
                <Icon size={17} strokeWidth={1.75} className="shrink-0" />
                <span className="flex-1 truncate">{label}</span>
                <Lock size={12} strokeWidth={2} className="shrink-0 text-qenva-blue/60 group-hover:text-qenva-blue" />
              </button>
            );
          }
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              end={to === "/dashboard"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] transition-all relative min-w-0 ${
                  isActive
                    ? "bg-qenva-blue/15 text-white font-semibold"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-full bg-qenva-blue" />
                  )}
                  <Icon size={17} strokeWidth={isActive ? 2.25 : 1.75} className="shrink-0" />
                  <span className="flex-1 truncate">{label}</span>
                </>
              )}
            </NavLink>
          );
        })}

        {/* Secondary nav — collapsed under a divider */}
        <div className="pt-3 mt-3 border-t border-white/10 space-y-0.5">
          {secondaryNavItems.map(({ to, label, Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-white/55 hover:bg-white/5 hover:text-white/90"
                }`
              }
            >
              <Icon size={15} strokeWidth={1.75} className="shrink-0" />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Surveillance footer pill */}
      <div className="mt-4 mb-3" data-testid="surveillance-active-pill">
        <div className="rounded-xl bg-qenva-green/10 border border-qenva-green/30 px-3.5 py-3">
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-qenva-green inline-flex items-center gap-1.5">
            <span className="relative flex w-1.5 h-1.5">
              <span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-qenva-green opacity-75" />
              <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-qenva-green" />
            </span>
            {t("layout.surveillance_active")}
          </p>
          <p className="text-[11px] text-white/60 mt-1">
            {t("layout.engagement_count", { count: 24 })}
          </p>
        </div>
      </div>

      {/* User / sign-out */}
      <div className="border-t border-white/10 pt-3">
        <div className="px-3 mb-3 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-0.5">{t("nav.signed_in_as")}</p>
          <p className="text-[13px] font-medium text-white truncate" data-testid="current-user-email" title={user?.email}>
            {user?.email || t("nav.guest")}
          </p>
        </div>

        <button
          onClick={handleSignOut}
          data-testid="sign-out-button"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] text-white/60 hover:bg-white/5 hover:text-white transition-colors"
        >
          <LogOut size={15} strokeWidth={1.75} />
          <span>{t("nav.sign_out")}</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-qenva-off text-qenva-navy">
      {/* Mobile top bar — official "Dark Background" lockup, compact size. */}
      <header className="lg:hidden sticky top-0 z-30 bg-qenva-navy text-white px-4 h-14 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center" data-testid="brand-link-mobile">
          <QenvaWordmark tone="dark" size={28} alt="QENVA" />
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher variant="header" testIdPrefix="language-switcher-mobile" />
          <HelpButton variant="mobile" onClick={() => setHelpPanelOpen(true)} />
          <button
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-button"
            className="w-9 h-9 grid place-items-center rounded-md hover:bg-white/10"
          >
            <Menu size={18} />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setOpen(false)}
            data-testid="mobile-menu-overlay"
          />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-qenva-navy text-white p-5 flex flex-col">
            <button
              onClick={() => setOpen(false)}
              className="absolute right-3 top-3 z-10 w-8 h-8 grid place-items-center rounded-md text-qenva-navy hover:bg-black/5"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X size={16} />
            </button>
            <NavInner variant="mobile" />
          </aside>
        </div>
      )}

      <div className="lg:flex">
        {/* Desktop sidebar — "natural stop" treatment:
           - sticky top-0 self-start keeps it pinned and prevents stretching
           - max-h-screen + overflow-y-auto allows internal scroll when content
             overflows the viewport
           - pb-3 + flex-col with a bottom fade gradient creates a smooth
             "more content below" cue when the user is mid-scroll, instead of
             the previous abrupt clipping right at the viewport bottom edge.
           - the gradient mask sits OUTSIDE the scroll area (sticky positioning)
             so it doesn't move when the sidebar's inner content scrolls.
        */}
        <div className="hidden lg:block sticky top-0 self-start shrink-0 w-[260px] xl:w-[280px] max-h-screen">
          <aside
            className="flex flex-col h-screen bg-qenva-navy text-white px-5 pt-6 pb-5 overflow-y-auto overscroll-contain border-r border-qenva-navy-line scrollbar-thin"
            data-testid="sidebar-desktop"
          >
            <NavInner variant="desktop" />
          </aside>
          {/* Bottom fade — sits over the bottom 24px of the viewport edge,
              same dark hue as the sidebar so scrolled content fades out
              naturally instead of cutting off. Pointer-events:none so it
              doesn't intercept clicks on the sign-out button. */}
          <div
            aria-hidden="true"
            data-testid="sidebar-bottom-fade"
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-qenva-navy via-qenva-navy/80 to-transparent"
          />
        </div>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <MfaGraceBanner />
          <EmailVerificationBanner />
          <div className="max-w-[1480px] mx-auto px-5 sm:px-7 lg:px-10 xl:px-12 py-8 lg:py-10">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Plan-gated upgrade modal — opened from locked nav items */}
      <UpgradeModal
        open={!!lockedItem}
        onClose={() => setLockedItem(null)}
        feature={lockedItem?.feature}
        Icon={lockedItem?.Icon}
      />

      {/* Help — deliberately kept OUTSIDE navItems/secondaryNavItems and
          outside NavInner so it can never match a react-joyride tour-step
          selector (tours target nav rows by their own data-testid/nth-child). */}
      <HelpButton variant="desktop" onClick={() => setHelpPanelOpen(true)} />
      <HelpPanel open={helpPanelOpen} onClose={() => setHelpPanelOpen(false)} />
    </div>
  );
}
